import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Paper,
  Grid,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import InfoIcon from '@mui/icons-material/Info';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {
  getCheckNoteRelatedTransactions,
  deleteCheckNoteWithTransactions,
  CheckNoteRelatedTransactions,
} from '../../../services/checkNoteService';
import ownAuthSession from '../../../services/auth/ownAuthSession';

interface CheckNoteDeleteConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirmDelete?: () => void;
  checkNoteId: number | null;
}

const CheckNoteDeleteConfirmModal: React.FC<CheckNoteDeleteConfirmModalProps> = ({
  open,
  onClose,
  onConfirmDelete,
  checkNoteId,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CheckNoteRelatedTransactions | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open && checkNoteId) {
      loadRelated();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, checkNoteId]);

  const loadRelated = async () => {
    if (!checkNoteId) return;
    setLoading(true);
    setError(null);
    try {
      console.log('🔍 [ÇEK SİL] İlişkili hareketler isteniyor. checkNoteId =', checkNoteId);
      const result = await getCheckNoteRelatedTransactions(checkNoteId);
      console.log('📦 [ÇEK SİL] API yanıtı (tam):', result);
      console.log('📊 [ÇEK SİL] Özet → Cari:', result.totalAccountTransactions,
        '| Banka:', result.totalBankTransactions,
        '| Kasa:', result.totalCashboxTransactions);
      console.log('👤 [ÇEK SİL] Cari hareket detayları:', result.accountTransactions);
      if ((result.totalAccountTransactions || 0) === 0) {
        console.warn('⚠️ [ÇEK SİL] Bu çek için CARİ hareket bulunamadı! '
          + 'Cari harekete ReferenceId=' + checkNoteId + ' ve ReferenceType=CHECK_NOTE yazılmamış olabilir. '
          + 'Backend loglarını (GetRelatedTransactionsAsync teşhis logu) kontrol edin.');
      }
      setData(result);
    } catch (err: any) {
      console.error('❌ [ÇEK SİL] Çek/Senet hareketleri yüklenirken hata:', err, err?.response?.data);
      setError(err.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'TRY') =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(amount || 0);

  const formatDate = (dateString?: string) =>
    dateString
      ? new Date(dateString).toLocaleDateString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit' })
      : '-';

  const getTypeText = (type?: string) => (type === 'CHECK' ? 'Çek' : type === 'PROMISSORY_NOTE' ? 'Senet' : type || '');
  const getDirectionText = (dir?: string) => (dir === 'RECEIVED' ? 'Alınan' : dir === 'GIVEN' ? 'Verilen' : dir || '');

  const handleDelete = async () => {
    if (!checkNoteId) return;
    setDeleting(true);
    setError(null);
    try {
      const deletedBy = ownAuthSession.getCurrentEmail() || ownAuthSession.getCurrentUid() || 'System';
      await deleteCheckNoteWithTransactions(checkNoteId, deletedBy);
      if (onConfirmDelete) onConfirmDelete();
      onClose();
    } catch (err: any) {
      console.error('❌ Çek/Senet silinirken hata:', err);
      const apiError = err.response?.data?.message || err.message;
      setError(apiError || 'Çek/Senet silinirken bir hata oluştu');
    } finally {
      setDeleting(false);
    }
  };

  const cn = data?.checkNote;
  const totalTransactions =
    (data?.totalAccountTransactions || 0) +
    (data?.totalBankTransactions || 0) +
    (data?.totalCashboxTransactions || 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{ bgcolor: 'error.main', color: 'white', display: 'flex', alignItems: 'center', gap: 2 }}
      >
        <WarningIcon fontSize="large" />
        <Box>
          <Typography variant="h6">Çek/Senet Silme Onayı</Typography>
          {cn && (
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {cn.documentNumber} - {getDirectionText(cn.direction)} {getTypeText(cn.type)}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            {/* Özet bilgi */}
            {cn && (
              <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Belge No:</Typography>
                    <Typography variant="body1" fontWeight="bold">{cn.documentNumber}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Cari Hesap:</Typography>
                    <Typography variant="body1" fontWeight="bold">{cn.accountName || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Tutar:</Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      {formatCurrency(cn.amount, cn.currency)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Vade Tarihi:</Typography>
                    <Typography variant="body1" fontWeight="bold">{formatDate(cn.dueDate)}</Typography>
                  </Grid>
                  {cn.endorsedToAccountName && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Ciro Edilen Cari:</Typography>
                      <Typography variant="body1" fontWeight="bold">{cn.endorsedToAccountName}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            )}

            {/* Uyarı */}
            <Alert severity="warning" icon={<InfoIcon />} sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>DİKKAT:</strong> Bu çek/seneti sildiğinizde, aşağıdaki{' '}
                <strong>{totalTransactions} adet</strong> hareket kaydı da silinecek ve ilgili cari/kasa/banka
                bakiyeleri geri düzeltilecektir. Bu işlem geri alınamaz.
              </Typography>
            </Alert>

            {/* Hareket özetleri */}
            {data && (
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        bgcolor: data.totalAccountTransactions > 0 ? 'warning.light' : 'grey.100',
                        color: data.totalAccountTransactions > 0 ? 'warning.dark' : 'text.secondary',
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4" fontWeight="bold">{data.totalAccountTransactions}</Typography>
                      <Typography variant="caption">Cari Hareket</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        bgcolor: data.totalBankTransactions > 0 ? 'warning.light' : 'grey.100',
                        color: data.totalBankTransactions > 0 ? 'warning.dark' : 'text.secondary',
                      }}
                    >
                      <AccountBalanceIcon sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4" fontWeight="bold">{data.totalBankTransactions}</Typography>
                      <Typography variant="caption">Banka Hareket</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        bgcolor: data.totalCashboxTransactions > 0 ? 'warning.light' : 'grey.100',
                        color: data.totalCashboxTransactions > 0 ? 'warning.dark' : 'text.secondary',
                      }}
                    >
                      <AccountBalanceWalletIcon sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4" fontWeight="bold">{data.totalCashboxTransactions}</Typography>
                      <Typography variant="caption">Kasa Hareket</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Detaylar */}
            {data && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
                  Hareket Detayları
                </Typography>

                {/* Cari Hareketler */}
                {data.accountTransactions.length > 0 && (
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <PersonIcon color="primary" />
                        <Typography fontWeight="bold">
                          Cari Hareketler ({data.accountTransactions.length})
                        </Typography>
                        <Chip
                          label={`Borç: ${formatCurrency(data.totalDebitAmount)} | Alacak: ${formatCurrency(data.totalCreditAmount)}`}
                          size="small"
                          sx={{ ml: 'auto' }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Cari Hesap</TableCell>
                            <TableCell>İşlem Tipi</TableCell>
                            <TableCell align="right">Borç</TableCell>
                            <TableCell align="right">Alacak</TableCell>
                            <TableCell>Açıklama</TableCell>
                            <TableCell>Tarih</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.accountTransactions.map((at) => (
                            <TableRow key={at.id}>
                              <TableCell>{at.accountName}</TableCell>
                              <TableCell>
                                <Chip label={at.transactionType} size="small" variant="outlined" />
                              </TableCell>
                              <TableCell align="right">{formatCurrency(at.debitAmount)}</TableCell>
                              <TableCell align="right">{formatCurrency(at.creditAmount)}</TableCell>
                              <TableCell sx={{ maxWidth: 240, whiteSpace: 'normal' }}>{at.description}</TableCell>
                              <TableCell>{formatDate(at.transactionDate)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Banka Hareketleri */}
                {data.bankTransactions.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <AccountBalanceIcon color="info" />
                        <Typography fontWeight="bold">
                          Banka Hareketleri ({data.bankTransactions.length})
                        </Typography>
                        <Chip label={formatCurrency(data.totalBankAmount)} size="small" color="info" sx={{ ml: 'auto' }} />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Banka Hesabı</TableCell>
                            <TableCell>İşlem Tipi</TableCell>
                            <TableCell align="right">Tutar</TableCell>
                            <TableCell>Açıklama</TableCell>
                            <TableCell>Tarih</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.bankTransactions.map((bt) => (
                            <TableRow key={bt.id}>
                              <TableCell>{bt.targetName}</TableCell>
                              <TableCell>
                                <Chip label={bt.transactionType} size="small" variant="outlined" color="info" />
                              </TableCell>
                              <TableCell align="right">{formatCurrency(bt.amount)}</TableCell>
                              <TableCell sx={{ maxWidth: 240, whiteSpace: 'normal' }}>{bt.description}</TableCell>
                              <TableCell>{formatDate(bt.transactionDate)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Kasa Hareketleri */}
                {data.cashboxTransactions.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <AccountBalanceWalletIcon color="success" />
                        <Typography fontWeight="bold">
                          Kasa Hareketleri ({data.cashboxTransactions.length})
                        </Typography>
                        <Chip label={formatCurrency(data.totalCashAmount)} size="small" color="success" sx={{ ml: 'auto' }} />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Kasa Adı</TableCell>
                            <TableCell>İşlem Tipi</TableCell>
                            <TableCell align="right">Tutar</TableCell>
                            <TableCell>Açıklama</TableCell>
                            <TableCell>Tarih</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.cashboxTransactions.map((ct) => (
                            <TableRow key={ct.id}>
                              <TableCell>{ct.targetName}</TableCell>
                              <TableCell>
                                <Chip label={ct.transactionType} size="small" variant="outlined" color="success" />
                              </TableCell>
                              <TableCell align="right">{formatCurrency(ct.amount)}</TableCell>
                              <TableCell sx={{ maxWidth: 240, whiteSpace: 'normal' }}>{ct.description}</TableCell>
                              <TableCell>{formatDate(ct.transactionDate)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                )}

                {totalTransactions === 0 && (
                  <Alert severity="info">
                    Bu çek/senete bağlı herhangi bir hareket kaydı bulunamadı. Güvenle silebilirsiniz.
                  </Alert>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={deleting}>
          İptal
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteForeverIcon />}
          disabled={loading || deleting}
        >
          {deleting ? 'Siliniyor...' : 'Çek/Seneti ve Tüm Hareketleri Sil'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckNoteDeleteConfirmModal;
